import { TargetPriceAllocator } from "../../../services/discount-distributors/allocators/target-price-allocator";
import { MixAndMatchBaseCoupon } from "./mix-and-match-base";
import { BeforeTaxDistributor } from "../../../../domain/services/discount-distributors/before-tax-distributor";
export class MixAndMatchPriceCoupon extends MixAndMatchBaseCoupon {
    getBeforeTaxDistributor() {
        return new BeforeTaxDistributor(new TargetPriceAllocator());
    }
    /**
     * The target price is enforced per qualifying item, so each item is measured on its own and the
     * results summed. A cart can hold both a whole item and a split one — the mix-and-match
     * qualifier takes every match it can — so the basis is chosen per item rather than per cart.
     */
    computeDiscountAmount(items) {
        let discountAmount = 0;
        // Only split portions are grouped. Whole items stay individual: a cart line with a quantity
        // above one can qualify more than once and yields several discount items sharing an itemKey,
        // each of which is its own qualifying item for the target price.
        const splitPortions = new Map();
        for (const item of items) {
            if ((item.halfCount ?? 1) > 1) {
                const portions = splitPortions.get(item.itemKey);
                if (portions)
                    portions.push(item);
                else
                    splitPortions.set(item.itemKey, [item]);
                continue;
            }
            discountAmount += this._wholeItemDiscount(item);
        }
        for (const portions of splitPortions.values())
            discountAmount += this._splitItemDiscount(portions);
        return discountAmount;
    }
    /**
     * A split item is driven down to the target price from what the customer is actually charged,
     * not from the summed covered-price shares (AC #82541, the same reasoning as the Fixed Price
     * price-point base). Summing the shares gives the average of the portions' covered prices, which
     * sits below the Setting 22 price whenever the portions differ, leaving the item above its target
     * price and the coupon under-discounted.
     *
     * Anything the target price does not buy comes back off: the add-on shares, the toppings a
     * portion carries beyond its own Toppings Covered, and modifiers that upcharge without being
     * priced modifiers (Bug 82910).
     */
    _splitItemDiscount(portions) {
        const halfCount = portions[0].halfCount ?? 1;
        const targetShare = this.discountValue / halfCount;
        const amount = portions.reduce((total, portion) => total
            + portion.oPrice
            - targetShare
            - portion.addOn
            - (portion.uncoveredModifierPrice ?? 0)
            - (portion.upchargeModifierPrice ?? 0), 0);
        return Math.max(0, amount);
    }
    _wholeItemDiscount(item) {
        if (!item.halfCount || item.cPrice <= (this.discountValue / item.halfCount))
            return 0;
        const targetShare = this.discountValue / item.halfCount;
        const pending = item.cPrice - targetShare - item.addOn;
        return (item.oPrice - pending) < targetShare
            ? item.oPrice - targetShare - item.addOn
            : pending;
    }
    getConfiguredDiscountValueForDistribution() {
        // For MIX_AND_MATCH_PRICE, CouponBase uses this as the configured target price.
        return this.discountValue;
    }
}
//# sourceMappingURL=mix-and-match-price.js.map